import React, { useEffect, useState } from "react";
import { Send, X } from "lucide-react";
import { toast } from "sonner";
import { createReply, fetchReplies } from "../../lib/communityApi";
import { formatSgDateTime, extractApiErrorMessage } from "../../lib/format";

const MAX_REPLY = 1000;

const ReplyThread = ({ token, post, onClose, onCountChange }) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchReplies(token, post.id)
      .then((data) => {
        if (!mounted) return;
        setReplies(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        console.error("fetchReplies failed:", err);
        toast.error("Could not load replies.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [token, post.id]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Write something kind before sending.");
      return;
    }
    setSubmitting(true);
    try {
      const reply = await createReply(token, post.id, { content: trimmed });
      setReplies((prev) => [...prev, reply]);
      setText("");
      if (typeof onCountChange === "function") {
        onCountChange(replies.length + 1);
      }
    } catch (err) {
      console.error("createReply failed:", err);
      toast.error(extractApiErrorMessage(err, "Could not send your reply."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 rounded-2xl border border-[#e6eef5] bg-[#f8fbfe] p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <p className="ht-body text-sm font-semibold text-[#3d6486]">
          Replies
        </p>
        <button
          onClick={onClose}
          className="text-[#9aa6b2] hover:text-[#2c3e50]"
          aria-label="Close replies"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {loading && (
          <p className="text-sm text-[#7e8a96]">Loading replies…</p>
        )}
        {!loading && replies.length === 0 && (
          <p className="text-sm text-[#7e8a96]">
            No replies yet. Be the first to gently say something.
          </p>
        )}
        {!loading &&
          replies.map((r) => (
            <div
              key={r.id}
              className="rounded-xl bg-white border border-[#e6eef5] p-3.5"
            >
              <div className="flex items-center justify-between">
                <p className="ht-body text-sm font-semibold text-[#2c3e50]">
                  {r?.author?.first_name || "Friend"}
                </p>
                <p className="text-xs text-[#7e8a96]">
                  {formatSgDateTime(r?.created_at)}
                </p>
              </div>
              <p className="mt-1.5 ht-body text-[#3d4e62] whitespace-pre-wrap leading-relaxed text-sm">
                {r?.content}
              </p>
            </div>
          ))}
      </div>

      <form onSubmit={submit} className="mt-4 flex gap-2 items-end">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_REPLY))}
          rows={2}
          placeholder="Reply gently…"
          className="ht-input flex-1 rounded-md border border-[#d6dde4] bg-white px-3 py-2 text-sm placeholder:text-[#9aa6b2] resize-y"
        />
        <button
          type="submit"
          disabled={submitting}
          className="ht-btn inline-flex items-center gap-1.5 rounded-md bg-[#3d6486] hover:bg-[#345674] text-white font-semibold px-3.5 py-2 text-sm disabled:opacity-70"
        >
          <Send className="h-4 w-4" />
          {submitting ? "Sending…" : "Send"}
        </button>
      </form>
      <p className="mt-1 text-[11px] text-[#9aa6b2] text-right">
        {text.length}/{MAX_REPLY}
      </p>
    </div>
  );
};

export default ReplyThread;
